// Composite agent's local + remote streams into ONE MediaStream that
// MediaRecorder can encode. Layout: remote stream fills the frame, local
// stream sits PiP top-right. Audio from both is mixed via WebAudio and
// captured as a single track.
//
// We hand-roll the composite (canvas captureStream + WebAudio mix) because
// browsers don't natively merge multiple MediaStreams into a single
// recorder input. Compositing happens on the main thread for now; if it
// becomes a perf bottleneck, port the draw loop into an OffscreenCanvas
// worker (Section 5.10.5 — currently out of scope).

export interface ComposerHandle {
  /** The composite stream — feed this into MediaRecorder. */
  stream: MediaStream;
  /** Swap the agent's own screen-share into the composite. Pass the
   *  getDisplayMedia stream while sharing — it becomes the MAIN frame and the
   *  remote (visitor) feed drops to the PiP. Pass null to revert to the
   *  default remote-main / local-PiP layout. Needed because screen-share is
   *  wired via RTCRtpSender.replaceTrack(), which only changes what the peer
   *  receives — it never touches the local MediaStream the recorder draws, so
   *  without this the agent's shared screen never reaches the recording. */
  setScreen: (stream: MediaStream | null) => void;
  /** Mark whether the VISITOR is currently screen-sharing. Their screen
   *  already arrives on the remote track (the widget uses replaceTrack), so
   *  this only drives the on-frame "<visitor> · screen" caption — it doesn't
   *  change which stream is drawn. */
  setRemoteSharing: (sharing: boolean) => void;
  /** Capture a still-frame snapshot of the composite canvas. Returns null
   *  when the canvas is empty (e.g. media not yet flowing). Useful for
   *  recording thumbnails — agent UI shows this for each call row. */
  snapshot: (mime?: string, quality?: number) => Promise<Blob | null>;
  /** Tear down canvas, audio graph, captured tracks. */
  stop: () => void;
}

/** Display names used for the on-frame "whose screen" caption. */
export interface ComposerLabels {
  /** The agent viewing/recording — labels their own shared screen. */
  agent?: string;
  /** The visitor on the call — labels their shared screen. */
  visitor?: string;
}

const WIDTH = 1280;
const HEIGHT = 720;
const FPS = 24;
// PiP box for the local stream.
const PIP_W = 240;
const PIP_H = 135;
const PIP_PAD = 16;

export function startComposite(
  remote: MediaStream,
  local: MediaStream,
  labels: ComposerLabels = {},
): ComposerHandle {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');

  // Off-screen <video> elements feed the canvas. They never attach to the
  // DOM, but srcObject keeps the decoder pipeline alive.
  const remoteVideo = document.createElement('video');
  remoteVideo.srcObject = remote;
  remoteVideo.muted = true;
  remoteVideo.playsInline = true;
  void remoteVideo.play().catch(() => {});
  // The remote feed's content swaps in place when the VISITOR shares their
  // screen (the widget uses RTCRtpSender.replaceTrack, so it's the same track
  // object — no `ontrack` re-fires). The dimensions jump camera→screen; a
  // detached <video> can leave a stalled frame after that swap, which would
  // freeze the visitor's screen out of the recording. Re-kick playback on
  // resize so the canvas keeps pulling fresh frames.
  remoteVideo.addEventListener('resize', () => {
    void remoteVideo.play().catch(() => {});
  });

  const localVideo = document.createElement('video');
  localVideo.srcObject = local;
  localVideo.muted = true;
  localVideo.playsInline = true;
  void localVideo.play().catch(() => {});

  // The agent's own screen-share, when active. getDisplayMedia content lands
  // here (set via setScreen) and is promoted to the MAIN frame.
  const screenVideo = document.createElement('video');
  screenVideo.muted = true;
  screenVideo.playsInline = true;
  let screenActive = false;
  // Set via setRemoteSharing() from the widget's screen:share hint — the
  // visitor's screen is already arriving on the remote track; this just tells
  // the caption which name to show.
  let remoteSharing = false;

  let raf = 0;
  function draw() {
    if (!ctx) {
      raf = requestAnimationFrame(draw);
      return;
    }
    // Background — paper-ink combo so dropped frames don't flash white.
    ctx.fillStyle = '#14110E';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // When the agent is sharing, the screen owns the main frame and the
    // remote (visitor) camera drops to the PiP. Otherwise the remote feed —
    // which already carries the visitor's screen when THEY share — fills it.
    const mainVideo = screenActive ? screenVideo : remoteVideo;
    const pipVideo = screenActive ? remoteVideo : localVideo;

    // Main — cover (preserve aspect, fill frame).
    drawCover(ctx, mainVideo, 0, 0, WIDTH, HEIGHT);

    // PiP top-right with a 1px hairline border.
    const x = WIDTH - PIP_W - PIP_PAD;
    const y = PIP_PAD;
    drawCover(ctx, pipVideo, x, y, PIP_W, PIP_H);
    ctx.strokeStyle = 'rgba(236, 229, 214, 0.6)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, PIP_W - 1, PIP_H - 1);

    // Caption the MAIN frame whenever a SCREEN (not a camera) fills it, so a
    // recording reviewer can see whose screen they're watching. The agent's
    // share wins if both share at once (it owns the main frame).
    const caption = screenActive
      ? `${labels.agent ?? 'Agent'} · screen`
      : remoteSharing
        ? `${labels.visitor ?? 'Visitor'} · screen`
        : null;
    if (caption) drawCaption(ctx, caption);

    raf = requestAnimationFrame(draw);
  }
  raf = requestAnimationFrame(draw);

  // Capture canvas → MediaStreamTrack (video).
  const canvasStream = canvas.captureStream(FPS);

  // ─── Audio mix ───
  // WebAudio merges both audio sources onto a single output node we then
  // capture as a MediaStreamTrack. Tracks without audio are skipped.
  const audioCtx = new AudioContext();
  // Chrome creates AudioContexts in 'suspended' state until a user gesture.
  // We're typically downstream of an Accept click, but the gesture window
  // has lapsed by the time WebRTC negotiation completes — kick it back on.
  void audioCtx.resume().catch(() => {});
  const dest = audioCtx.createMediaStreamDestination();
  const sourceNodes: MediaStreamAudioSourceNode[] = [];
  // Hidden <audio> sinks. Chrome has a long-standing quirk where a remote
  // WebRTC stream piped straight into createMediaStreamSource emits silence
  // unless something is also consuming its audio for playback. Attaching a
  // muted <audio> element forces the decode pipeline to stay alive.
  const sinks: HTMLAudioElement[] = [];

  function pipeAudio(s: MediaStream) {
    if (s.getAudioTracks().length === 0) return;
    const sink = document.createElement('audio');
    sink.srcObject = s;
    sink.muted = true;
    sink.autoplay = true;
    void sink.play().catch(() => {});
    sinks.push(sink);
    const node = audioCtx.createMediaStreamSource(s);
    node.connect(dest);
    sourceNodes.push(node);
  }
  pipeAudio(remote);
  pipeAudio(local);

  // Combine canvas video + mixed audio into the final composite stream.
  const composite = new MediaStream();
  canvasStream.getVideoTracks().forEach((t) => composite.addTrack(t));
  dest.stream.getAudioTracks().forEach((t) => composite.addTrack(t));

  function snapshot(mime: string = 'image/jpeg', quality: number = 0.78): Promise<Blob | null> {
    return new Promise((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), mime, quality);
      } catch {
        resolve(null);
      }
    });
  }

  function setScreen(stream: MediaStream | null): void {
    if (stream) {
      screenVideo.srcObject = stream;
      void screenVideo.play().catch(() => {});
      screenActive = true;
    } else {
      screenActive = false;
      // Drop the reference but DON'T stop the tracks — Conversation owns the
      // getDisplayMedia stream and stops it when sharing ends.
      screenVideo.srcObject = null;
    }
  }

  function setRemoteSharing(sharing: boolean): void {
    remoteSharing = sharing;
  }

  return {
    stream: composite,
    setScreen,
    setRemoteSharing,
    snapshot,
    stop: () => {
      cancelAnimationFrame(raf);
      canvasStream.getTracks().forEach((t) => t.stop());
      composite.getTracks().forEach((t) => t.stop());
      sourceNodes.forEach((n) => {
        try {
          n.disconnect();
        } catch {
          /* noop */
        }
      });
      sinks.forEach((s) => {
        try {
          s.pause();
        } catch {
          /* noop */
        }
        s.srcObject = null;
      });
      void audioCtx.close().catch(() => {});
      remoteVideo.srcObject = null;
      localVideo.srcObject = null;
      screenVideo.srcObject = null;
    },
  };
}

// A pill-shaped caption anchored bottom-left of the main frame.
function drawCaption(ctx: CanvasRenderingContext2D, text: string): void {
  ctx.save();
  ctx.font = '600 24px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  ctx.textBaseline = 'middle';
  const padX = 16;
  const boxH = 44;
  const textW = ctx.measureText(text).width;
  const boxW = textW + padX * 2;
  const bx = PIP_PAD;
  const by = HEIGHT - PIP_PAD - boxH;
  const r = 10;
  // Rounded-rect background.
  ctx.beginPath();
  ctx.moveTo(bx + r, by);
  ctx.arcTo(bx + boxW, by, bx + boxW, by + boxH, r);
  ctx.arcTo(bx + boxW, by + boxH, bx, by + boxH, r);
  ctx.arcTo(bx, by + boxH, bx, by, r);
  ctx.arcTo(bx, by, bx + boxW, by, r);
  ctx.closePath();
  ctx.fillStyle = 'rgba(20, 17, 14, 0.7)';
  ctx.fill();
  ctx.fillStyle = '#F6F3EC';
  ctx.fillText(text, bx + padX, by + boxH / 2 + 1);
  ctx.restore();
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) {
    // Placeholder fill while metadata loads.
    ctx.fillStyle = '#1C1814';
    ctx.fillRect(dx, dy, dw, dh);
    return;
  }
  // CSS object-fit: cover semantics.
  const scale = Math.max(dw / vw, dh / vh);
  const cw = vw * scale;
  const ch = vh * scale;
  const cx = dx + (dw - cw) / 2;
  const cy = dy + (dh - ch) / 2;
  ctx.drawImage(video, cx, cy, cw, ch);
}
